"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatePicker = exports.Overlay = void 0;
const react_1 = __importStar(require("react"));
const styled_components_1 = __importDefault(require("styled-components"));
const moment_1 = __importDefault(require("moment"));
const lodash_1 = require("lodash");
const theme_1 = __importDefault(require("../../styles/theme"));
const types_1 = require("../../lib/types");
const date_utils_1 = __importDefault(require("../../lib/date-utils"));
const icon_1 = require("../icon");
const text_input_1 = __importDefault(require("../inputs/text-input"));
const calendar_modal_1 = __importDefault(require("./calendar-modal"));
exports.Overlay = styled_components_1.default.span `
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;
const StyledField = styled_components_1.default.div `
  position: relative;
  width: 100%;
`;
const isNullDate = (checkedDate) => checkedDate &&
    checkedDate.year === null &&
    checkedDate.month === null &&
    checkedDate.day === null;
/**
 *
 * DatePicker Usage Example:
 *
 * ```
 * const [dateValue, setDateValue] = useState('2020-03-21');
 *
 *  <DatePicker
 *    selected={dateValue}         // Expected format: YYYY-MM-DD
 *    onDateSelect={setDateValue}
 *    label="Date of Birth"
 *    minDate="1980-01-01"         // Default: January 1st, 110 years ago
 *    maxDate="2020-02-21"         // Default: December 31st, 20 years ahead
 *  />
 * ```
 *
 * NOTE:
 * The DatePicker is composed of a text input and a calendar popup
 *
 * The text input field will display the date in format MM-DD-YYYY
 * However, the value passed in, dateValue, is updated via onDateSelect only when a date is clicked
 * This value will remain in format YYYY-MM-DD
 *
 *
 * WARNING: Before changing this file, please strongly consider whether your change is
 * necessary (we want to avoid unexpected bugs in other views).
 * This code is meant to be shared by many views throughout our app and should not
 * contain logic specifc to how your view is using it.
 *
 */
const DatePicker = ({ ariaLabel, className, disabled = false, displayFormat = 'MM-DD-YYYY', hint, id, label, locale = 'en', selected = '', onDateSelect, onBlur = lodash_1.noop, onFocus = lodash_1.noop, onClick = lodash_1.noop, maxDate = '', minDate = '', name, clearable = false, error = false, required, style, }) => {
    const [datePickerOpen, setDatePickerOpen] = react_1.useState(false);
    types_1.accessibleLabelCheck(ariaLabel, label);
    // initialize to selected value or the min value if passed in, otherwise current date (format: YYYY-MM-DD)
    const startingDate = selected || minDate;
    const initValue = startingDate
        ? moment_1.default(startingDate, 'YYYY-MM-DD')
        : moment_1.default();
    const initDate = {
        day: date_utils_1.default.getFormatAsNum(initValue, 'DD'),
        month: date_utils_1.default.getFormatAsNum(initValue, 'MM'),
        year: date_utils_1.default.getFormatAsNum(initValue, 'YYYY'),
    };
    const nullDate = { day: null, month: null, year: null };
    const [isLocaleLoading, setLocaleLoading] = react_1.useState(false);
    const [viewingDate, setViewingDate] = react_1.useState(initDate);
    const [selectedDate, setSelectedDate] = react_1.useState(selected ? initDate : nullDate);
    react_1.useEffect(() => {
        if (locale !== 'en') {
            setLocaleLoading(true);
            Promise.resolve().then(() => __importStar(require(`moment/locale/${locale}`))).catch(() => console.warn('Failed to load locale: ', locale)) // eslint-disable-line no-console
                .finally(() => setLocaleLoading(false));
        }
    }, [locale]);
    // default: end of year, 20 years ahead
    const maxDateMoment = maxDate
        ? moment_1.default(maxDate, 'YYYY-MM-DD')
        : moment_1.default().add(20, 'year').endOf('year');
    // default: start of year, 100 years ago
    const minDateMoment = minDate
        ? moment_1.default(minDate, 'YYYY-MM-DD')
        : moment_1.default().subtract(110, 'year').startOf('year');
    const resetDatePicker = () => {
        setDatePickerOpen(false);
        setViewingDate(initDate);
    };
    const clearSelectedDate = () => {
        const todayDate = moment_1.default();
        // clear date
        onDateSelect('');
        setSelectedDate(nullDate);
        // reset date to show on re-opening modal to today
        setViewingDate({
            day: date_utils_1.default.getFormatAsNum(todayDate, 'DD'),
            month: date_utils_1.default.getFormatAsNum(todayDate, 'MM'),
            year: date_utils_1.default.getFormatAsNum(todayDate, 'YYYY'),
        });
    };
    const handleDateSelection = (value) => {
        onDateSelect(value);
        setDatePickerOpen(false);
    };
    const textInputValue = selected && !isNullDate(selectedDate)
        ? moment_1.default({
            year: selectedDate.year || 0,
            month: selectedDate.month ? selectedDate.month - 1 : 0,
            date: selectedDate.day || 0,
        }).format(displayFormat)
        : '';
    return (react_1.default.createElement(StyledField, { className: className, style: style },
        react_1.default.createElement(text_input_1.default, { ariaLabel: ariaLabel, clearable: clearable, disabled: disabled || isLocaleLoading, label: label, hint: hint, value: textInputValue, error: error, name: name, onChange: (value) => {
                if (value === '') {
                    clearSelectedDate();
                }
            }, onClick: () => {
                setDatePickerOpen(true);
                onClick();
            }, onBlur: onBlur, onFocus: onFocus, icon: react_1.default.createElement(icon_1.IconButton, { ariaLabel: "Open date picker", disabled: disabled || isLocaleLoading, name: "calendar_today", color: theme_1.default.colors.textDark, onClick: () => setDatePickerOpen(true) }), iconPosition: "left", id: id, readOnly: true, required: required }),
        datePickerOpen && (react_1.default.createElement(react_1.default.Fragment, null,
            react_1.default.createElement(exports.Overlay, { "data-testid": "date-picker-overlay", onClick: resetDatePicker }),
            react_1.default.createElement(calendar_modal_1.default, { viewingDate: viewingDate, selectedDate: selectedDate, onViewingDateChange: setViewingDate, onSelectedDateChange: setSelectedDate, handleInputChange: handleDateSelection, maxDate: maxDateMoment, minDate: minDateMoment, locale: locale })))));
};
exports.DatePicker = DatePicker;
exports.default = exports.DatePicker;
